import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import type { Product } from '../../types/product';
import QuickViewPanel from './QuickViewPanel';

export default function Navbar() {
  const [cartCount, setCartCount] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [quickViewProductId, setQuickViewProductId] = useState<string | null>(null);
  // Removed TypeScript generic to keep the file valid JavaScript/JSX
  const searchRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Search products from Supabase
  useEffect(() => {
    const searchProducts = async () => {
      if (searchQuery.trim().length === 0) {
        setFilteredProducts([]);
        return;
      }

      setIsSearching(true);
      try {
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .or(`name.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%,category.ilike.%${searchQuery}%`)
          .limit(8);

        if (error) throw error;
        setFilteredProducts(data || []);
      } catch (err) {
        console.error('Error searching products:', err);
        setFilteredProducts([]);
      } finally {
        setIsSearching(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      searchProducts();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    const updateCounts = () => {
      try {
        const stored = localStorage.getItem('cart') ?? '[]';
        const cart = JSON.parse(stored);
        const totalItems = cart.reduce((sum, item) => sum + (item?.quantity ?? 0), 0);
        setCartCount(totalItems);
      } catch (e) {
        console.error('Failed to parse cart from localStorage', e);
        setCartCount(0);
      }
    };

    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
      if (mobileSearchRef.current && !mobileSearchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('storage', updateCounts);
    window.addEventListener('cartUpdated', updateCounts);
    document.addEventListener('mousedown', handleClickOutside);
    updateCounts();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('storage', updateCounts);
      window.removeEventListener('cartUpdated', updateCounts);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setSearchQuery('');
    setShowResults(false);
    setIsSearchOpen(false);
  }, [location.pathname]);

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleProductClick = (id: string) => {
    setSearchQuery('');
    setShowResults(false);
    setIsSearchOpen(false);
    setQuickViewProductId(id);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowResults(false);
      setIsSearchOpen(false);
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/products', label: 'Products' },
    { to: '/order-tracking', label: 'Track Order' },
  ];

  const SearchResults = () => {
    if (!showResults || searchQuery.trim().length === 0) return null;
    
    return (
      <div className="absolute top-full left-0 right-0 mt-1.5 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-[60] max-h-[360px] overflow-y-auto">
        {isSearching ? (
          <div className="px-4 py-8 text-center">
            <div className="w-6 h-6 border-2 border-[#143D28]/20 border-t-[#143D28] rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-sm text-gray-400">Recherche en cours...</p>
          </div>
        ) : filteredProducts.length > 0 ? (
          <>
            <div className="px-4 py-2.5 bg-gray-50/80 border-b border-gray-100">
              <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                {filteredProducts.length} résultat{filteredProducts.length > 1 ? 's' : ''}
              </span>
            </div>
            {filteredProducts.map((product) => (
              <button
                key={product.id}
                onClick={() => handleProductClick(product.id)}
                className="w-full flex items-center gap-3.5 px-4 py-3 hover:bg-[#143D28]/5 transition-colors cursor-pointer border-b border-gray-50 last:border-b-0"
              >
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{product.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[11px] text-[#143D28]/60 font-medium uppercase">{product.category}</span>
                    <span className="text-[11px] text-gray-300">•</span>
                    <span className="text-sm font-semibold text-[#D4A574]">${product.price.toFixed(2)}</span>
                  </div>
                </div>
                <i className="ri-arrow-right-s-line text-gray-300 text-lg w-5 h-5 flex items-center justify-center"></i>
              </button>
            ))}
          </>
        ) : (
          <div className="px-4 py-8 text-center">
            <i className="ri-search-line text-2xl text-gray-200 mb-2 block w-8 h-8 flex items-center justify-center mx-auto"></i>
            <p className="text-sm text-gray-400">Aucun produit trouvé</p>
            <p className="text-xs text-gray-300 mt-1">Essayez un autre mot-clé</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Main Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-[#143D28]/98 backdrop-blur-md shadow-[0_4px_30px_rgba(0,0,0,0.3)]' 
          : 'bg-[#143D28]'
      }`}>
        {/* Decorative top border */}
        <div className="h-[1px] bg-gradient-to-r from-transparent via-[#D4A574]/40 to-transparent"></div>
        
        <div className="w-full px-6 lg:px-10">
          <div className="flex items-center justify-between h-[72px]">
            
            {/* Left: Logo */}
            <Link to="/" className="flex flex-col items-start group flex-shrink-0" onClick={closeMobileMenu}>
              <div className="flex flex-col leading-none">
                <span className="text-[8px] uppercase tracking-[3px] text-[#D4A574]/60 font-medium mb-0.5">Maison</span>
                <span className="text-[20px] sm:text-[22px] font-semibold text-white font-serif tracking-wider group-hover:text-[#D4A574] transition-colors duration-500">
                  Daliah Secret
                </span>
              </div>
            </Link>

            {/* Center: Navigation Links + Search (Desktop) */}
            <div className="hidden md:flex items-center gap-1 flex-1 justify-center">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`relative px-6 py-2.5 text-[12px] font-medium tracking-[1.5px] uppercase transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    isActive(link.to)
                      ? 'text-[#D4A574]'
                      : 'text-white/70 hover:text-white'
                  }`}
                >
                  {link.label}
                  {isActive(link.to) && (
                    <span className="absolute bottom-0.5 left-6 right-6 h-[1px] bg-gradient-to-r from-transparent via-[#D4A574] to-transparent"></span>
                  )}
                </Link>
              ))}

              {/* Desktop Search Bar */}
              <div ref={searchRef} className="relative ml-4">
                <form onSubmit={handleSearchSubmit} className="relative">
                  <div className="flex items-center bg-white/10 hover:bg-white/15 focus-within:bg-white/20 rounded-full transition-all duration-300 border border-white/10 focus-within:border-[#D4A574]/30">
                    <i className="ri-search-line text-white/40 text-sm ml-3.5 w-4 h-4 flex items-center justify-center"></i>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setShowResults(true);
                      }}
                      onFocus={() => searchQuery.trim().length > 0 && setShowResults(true)}
                      placeholder="Rechercher un produit..."
                      className="bg-transparent text-white text-sm placeholder-white/30 px-3 py-2 w-[200px] lg:w-[240px] focus:outline-none"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => { setSearchQuery(''); setShowResults(false); }}
                        className="mr-2 w-5 h-5 flex items-center justify-center text-white/30 hover:text-white/60 transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line text-sm"></i>
                      </button>
                    )}
                  </div>
                </form>
                <SearchResults />
              </div>
            </div>

            {/* Right: Search toggle (mobile) + Cart + Mobile Menu */}
            <div className="flex items-center gap-1">
              {/* Mobile Search Toggle */}
              <button
                onClick={() => { setIsSearchOpen(!isSearchOpen); setShowResults(false); setSearchQuery(''); }}
                className="md:hidden w-10 h-10 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/5 transition-all duration-300 cursor-pointer"
                aria-label="Rechercher"
              >
                <i className={`${isSearchOpen ? 'ri-close-line' : 'ri-search-line'} text-lg`}></i>
              </button>

              {/* Cart */}
              <Link to="/cart" className="relative w-10 h-10 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/5 transition-all duration-300 cursor-pointer">
                <i className="ri-shopping-bag-2-line text-lg"></i>
                {cartCount > 0 && (
                  <span className="absolute top-0.5 right-0.5 w-[17px] h-[17px] bg-[#D4A574] text-[#0B2B1A] text-[9px] font-bold rounded-full flex items-center justify-center ring-2 ring-[#143D28]">
                    {cartCount}
                  </span>
                )}
              </Link>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden w-9 h-9 flex items-center justify-center text-white/80 hover:text-[#D4A574] transition-colors cursor-pointer"
                aria-label="Menu"
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-4-line'} text-xl`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Search Bar (slides down) */}
        <div className={`md:hidden overflow-hidden transition-all duration-300 ${isSearchOpen ? 'max-h-[70px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div ref={mobileSearchRef} className="relative px-4 pb-3">
            <form onSubmit={handleSearchSubmit} className="relative">
              <div className="flex items-center bg-white/10 rounded-full border border-white/10 focus-within:border-[#D4A574]/30">
                <i className="ri-search-line text-white/40 text-sm ml-3.5 w-4 h-4 flex items-center justify-center"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowResults(true);
                  }}
                  onFocus={() => searchQuery.trim().length > 0 && setShowResults(true)}
                  placeholder="Rechercher un produit..."
                  className="bg-transparent text-white text-sm placeholder-white/30 px-3 py-2.5 w-full focus:outline-none"
                  autoFocus={isSearchOpen}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => { setSearchQuery(''); setShowResults(false); }}
                    className="mr-3 w-5 h-5 flex items-center justify-center text-white/30 hover:text-white/60 transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                )}
              </div>
            </form>
            <SearchResults />
          </div>
        </div>

        {/* Bottom decorative border */}
        <div className="h-[1px] bg-gradient-to-r from-transparent via-[#D4A574]/20 to-transparent"></div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
          onClick={closeMobileMenu}
        ></div>
      )}

      {/* Mobile Menu Drawer */}
      <div
        className={`fixed top-0 left-0 bottom-0 w-[280px] bg-[#0F2E1E] z-50 transform transition-transform duration-400 ease-out md:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-[#D4A574]/10">
            <div className="flex flex-col leading-none">
              <span className="text-[7px] uppercase tracking-[3px] text-[#D4A574]/50 font-medium">Maison</span>
              <span className="text-lg font-semibold text-white font-serif tracking-wide">Daliah Secret</span>
            </div>
            <button
              onClick={closeMobileMenu}
              className="w-9 h-9 flex items-center justify-center rounded-full text-white/40 hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* Mobile Menu Content */}
          <div className="flex-1 overflow-y-auto py-4">
            <div className="px-4 space-y-0.5">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={closeMobileMenu}
                  className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium transition-all cursor-pointer ${
                    isActive(link.to)
                      ? 'bg-[#D4A574]/10 text-[#D4A574]'
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/cart"
                onClick={closeMobileMenu}
                className={`flex items-center justify-between px-4 py-3.5 rounded-xl text-sm font-medium transition-all cursor-pointer ${
                  isActive('/cart')
                    ? 'bg-[#D4A574]/10 text-[#D4A574]'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                <span className="flex items-center gap-2">
                  <i className="ri-shopping-bag-2-line text-base w-5 h-5 flex items-center justify-center"></i>
                  Panier
                </span>
                {cartCount > 0 && (
                  <span className="w-5 h-5 bg-[#D4A574] text-[#0B2B1A] text-[10px] font-semibold rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Spacer */}
      <div className="h-[74px]"></div>

      {/* Quick View Panel */}
      <QuickViewPanel
        productId={quickViewProductId}
        onClose={() => setQuickViewProductId(null)}
      />
    </>
  );
}
